import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { configFirebase, REGION, usaEmulador } from "./config";
import { registrarAccion } from "./analitica";

export const app = initializeApp(usaEmulador ? { ...configFirebase, projectId: "demo-porteria", apiKey: "demo-clave" } : configFirebase);
export const auth = getAuth(app);
auth.languageCode = "es";
// Con caché local la app abre al instante y sigue mostrando datos sin señal (garita, sótanos).
export const db = initializeFirestore(app, { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) });
export const funciones = getFunctions(app, REGION);
export const almacen = getStorage(app);

if (usaEmulador) {
  const host = location.hostname;
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8080);
  connectFunctionsEmulator(funciones, host, 5001);
  connectStorageEmulator(almacen, host, 9199);
}

/**
 * Llama una Cloud Function y devuelve su respuesta. Los errores traen el mensaje en español de la función,
 * su código (`functions/…`) y los `details` que mande el servidor (p. ej. el total nuevo de una cotización).
 * `timeout` (ms) corta la espera antes de los 70 s del SDK: útil con señal débil.
 */
export async function llamar<T = { ok: boolean }>(nombre: string, datos: Record<string, unknown> = {}, opciones: { timeout?: number } = {}): Promise<T> {
  try {
    const r = await httpsCallable(funciones, nombre, opciones)(datos);
    registrarAccion(nombre, datos);
    return r.data as T;
  } catch (e) {
    const err = e as { code?: string; details?: unknown };
    registrarAccion(nombre, datos, err);
    throw Object.assign(new Error(mensajeDeError(e)), { code: err.code, details: err.details, red: esErrorDeRed(e) });
  }
}

/**
 * El servidor rechazó el dato y reintentar no lo arregla (apto inexistente, campo inválido, ya registrado…).
 * Cualquier otro código (internal, unavailable, deadline-exceeded, unauthenticated, permission-denied…) puede
 * ser un fallo de red o pasajero: el SDK convierte un fetch caído en `functions/internal` aunque el celular
 * diga que está en línea.
 */
export function esErrorDeNegocio(e: unknown): boolean {
  const codigo = ((e as { code?: string })?.code ?? "").replace(/^functions\//, "");
  return ["invalid-argument", "not-found", "failed-precondition", "already-exists"].includes(codigo);
}

/**
 * El SDK de Functions agrega el estado HTTP al final del mensaje ("El valor cambió a $5.000… [400]").
 * Con estado 0 la petición ni llegó al servidor (sin señal o conexión cortada).
 */
const ESTADO_HTTP = /\s*\[(\d{1,3})\]$/;
const sinEstadoHttp = (mensaje: string) => mensaje.replace(ESTADO_HTTP, "");
const sinRespuesta = (e: unknown) => ESTADO_HTTP.exec((e as { message?: string })?.message ?? "")?.[1] === "0";

/** Error por falta de señal (vale la pena reintentar), no por una regla de negocio. */
export function esErrorDeRed(e: unknown): boolean {
  const codigo = (e as { code?: string })?.code;
  return (
    !navigator.onLine ||
    codigo === "functions/unavailable" ||
    codigo === "functions/deadline-exceeded" ||
    (codigo === "functions/internal" && sinRespuesta(e)) ||
    (e as { red?: boolean })?.red === true
  );
}

export function mensajeDeError(e: unknown): string {
  const err = e as { code?: string; message?: string };
  if (err?.code === "functions/unavailable" || err?.code === "unavailable" || !navigator.onLine) {
    return "Sin conexión. Revisa tu internet e intenta de nuevo.";
  }
  if (err?.code === "functions/deadline-exceeded" || err?.code === "deadline-exceeded") return "La señal está muy lenta. Intenta de nuevo.";
  if (err?.code === "functions/internal" && sinRespuesta(e)) return "No pudimos comunicarnos con el servidor. Revisa tu señal e intenta de nuevo.";
  const mensaje = sinEstadoHttp(err?.message ?? String(e));
  if (err?.code === "functions/internal" && mensaje === "internal") return "Algo salió mal. Intenta de nuevo en un momento.";
  return mensaje.replace(/^Firebase: /, "").replace(/\s*\(auth\/[a-z-]+\)\.?$/, "");
}
